import { apiClient } from "../api/client";

/** The rfq_items columns the item row writes. */
export interface RFQItemUpdate {
  item_name?: string;
  model_number?: string;
  quoted_price?: number;
  markup_percentage?: number | null;
  indent_days?: number;
  remark_tags?: string[];
  remarks?: string | null;
  saved_remark_id?: number | null;
  price_validated?: boolean;
  message_validated?: boolean;
  validated_by?: string | null;
  validated_at?: string | null;
}

/**
 * A failed backend write as a plain Error carrying the server's own message
 * (the Postgres message on a refused write), the way the direct Supabase
 * writes these calls replaced used to fail - so error toasts read as before.
 */
export function writeError(err: unknown): Error {
  const e = err as { message?: string; response?: { data?: { error?: string } } };
  return new Error(e?.response?.data?.error || e?.message || "Server communication error");
}

/**
 * Update one rfq_items row through the backend (validated and audited).
 * The backend writes exactly these columns, as given.
 */
export async function updateRfqItem(rfqId: number, rfqItemId: number, data: RFQItemUpdate) {
  try {
    const response = await apiClient.put(`/rfqs/${rfqId}/items/${rfqItemId}`, data);
    return response.data?.data;
  } catch (err) {
    throw writeError(err);
  }
}

/** Write the same columns to several items of one RFQ, in one backend call. */
export async function bulkUpdateRfqItems(rfqId: number, itemIds: number[], updates: RFQItemUpdate) {
  try {
    const response = await apiClient.post(`/rfqs/${rfqId}/items/bulk-update`, { itemIds, updates });
    return response.data as { updated: number[] };
  } catch (err) {
    throw writeError(err);
  }
}

/**
 * Fill a missing rfq_files.file_size from the Storage object's metadata. The
 * backend reads the size from Storage itself.
 */
export async function repairRfqFileSize(fileId: number): Promise<{ error: Error | null }> {
  try {
    await apiClient.post(`/rfq-files/${fileId}/repair-size`);
    return { error: null };
  } catch (err) {
    return { error: writeError(err) };
  }
}
